/**
 * FauxDS Search
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Search component for prototyping.
 * It is NOT the real @faire/slate Search and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Search, see: @faire/slate/components/search
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef, useState, useCallback, useEffect } from "react";

export interface SearchProps {
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Current value (controlled)
   */
  value?: string;
  /**
   * **playground only**
   * Default value (uncontrolled)
   */
  defaultValue?: string;
  /**
   * Change handler
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Focus handler
   */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Clear handler - if provided, clear button will show when there's a value
   */
  onClear?: () => void;
  /**
   * Whether the input is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * **playground only**
   * Input name attribute
   */
  name?: string;
  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

// Search icon
const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <circle cx="7" cy="7" r="5" />
    <path d="M11 11l3.5 3.5" strokeLinecap="round" />
  </svg>
);

// Clear/close icon
const ClearIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
  </svg>
);

/**
 * Search - FauxDS (recreated from Slate)
 *
 * Search Inputs allow users to quickly and easily find what they are looking for.
 * This is a simplified implementation for prototyping purposes.
 */
const Search = forwardRef<HTMLInputElement, SearchProps>(
  (
    {
      placeholder = "Search...",
      value,
      defaultValue,
      onChange,
      onFocus,
      onClear,
      disabled = false,
      name,
      className = "",
    },
    ref
  ) => {
    const [hasValue, setHasValue] = useState(!!value || !!defaultValue);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setHasValue(!!e.target.value);
        onChange?.(e);
      },
      [onChange]
    );

    const handleClear = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onClear?.();
      },
      [onClear]
    );

    // Sync hasValue with controlled value
    useEffect(() => {
      setHasValue(!!value);
    }, [value]);

    const showClearButton = onClear && hasValue && !disabled;

    return (
      <div className={cn("relative", className)}>
        {/* Search icon */}
        <div className="pointer-events-none absolute top-1/2 left-[12px] -translate-y-1/2 text-[var(--slate-color-icon-primary)]">
          <SearchIcon />
        </div>

        {/* Input */}
        <input
          ref={ref}
          type="search"
          name={name}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onFocus={onFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "h-[40px] w-full pl-[40px]",
            ["pr-[", showClearButton ? "40px" : "16px", "]"].join(""),
            "rounded-full border border-[var(--slate-color-action-border-subdued)] bg-[var(--slate-color-action-surface-inverse)] font-sans text-[14px] leading-[20px] text-[var(--slate-color-text-primary)] transition-all duration-150 ease-in-out placeholder:text-[var(--slate-color-action-text-placeholder)]",
            disabled
              ? "cursor-not-allowed border-[var(--slate-color-action-border-disabled)] bg-[var(--slate-color-action-surface-disabled)] text-[var(--slate-color-text-subdued)]"
              : "",
            "focus:text-[var(--slate-color-action-text-active)] focus:outline focus:outline-1 focus:-outline-offset-1 focus:outline-[var(--slate-color-action-border-active)] [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
          )}
        />

        {/* Clear button */}
        {showClearButton && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              "absolute top-1/2 right-[12px] flex -translate-y-1/2 cursor-pointer items-center justify-center rounded-sm p-1 text-[var(--slate-color-icon-primary)] hover:text-[var(--slate-color-text-primary)] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)]"
            )}
            aria-label="Clear search"
          >
            <ClearIcon />
          </button>
        )}
      </div>
    );
  }
);

Search.displayName = "Search";

export default Search;
